'use client';

import React from 'react';

import { usePathname } from 'next/navigation';

import Container from '@repo/ui/container';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@repo/ui/navigation-menu';

import { toClassName } from 'helpers/format';

import { GeneralSettings, SocialsSettings } from 'types/sanity.types';

import { useTranslation } from 'hooks/use-translation';

// import Banner from 'components/banner';
import Link from 'components/nextjs-fixes/link';
import SideMenu from 'components/side-menu';

import LogoSection from './_components/logo-section';

import { styles } from '.';

type TProps = {
  locale: string;

  general?: GeneralSettings;
  socials?: SocialsSettings[];

  routes: {
    id: string;
    label: string;
    url: string;
    subroutes?: { id: string; label: string; url: string }[];
  }[];
};

const NavBar = (props: TProps) => {
  const { locale, general, routes, socials } = props;

  const { t } = useTranslation(locale);

  const pathname = usePathname();

  return (
    <>
      <div className={styles.container}>
        <Container>
          <div className={styles.content}>
            <div className="flex items-center">
              <SideMenu
                logoUrl={general?.logo.asset?.url}
                routes={routes}
                locale={locale}
                socials={socials}
              />
              <LogoSection logoUrl={general?.logo.asset?.url} locale={locale} />
            </div>

            <div className="w-full hidden xl:block">
              <NavigationMenu viewport={false}>
                <NavigationMenuList>
                  {routes.map((route) => {
                    if (
                      route.subroutes === undefined ||
                      route.subroutes?.length === 0
                    ) {
                      return (
                        <NavigationMenuItem key={route.id}>
                          <NavigationMenuLink
                            asChild
                            className={toClassName(
                              styles.link,
                              pathname.includes(route.url) &&
                                '!text-gray-300 bg-gray-50',
                            )}
                          >
                            <Link href={route.url}>{route.label}</Link>
                          </NavigationMenuLink>
                        </NavigationMenuItem>
                      );
                    }

                    return (
                      <NavigationMenuItem key={route.id}>
                        <NavigationMenuTrigger
                          className={toClassName(
                            styles.link,
                            route.url === `/${locale}/about` &&
                              pathname.includes('/about') &&
                              '!font-semibold !text-red-500',
                          )}
                        >
                          {route.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid w-[300px] gap-4">
                            <li>
                              {route.subroutes?.map((subroute) => (
                                <NavigationMenuLink
                                  key={subroute.id}
                                  asChild
                                  className={toClassName(
                                    styles.link,

                                    pathname.includes(subroute.url) &&
                                      '!font-semibold',
                                  )}
                                >
                                  <Link href={subroute.url}>
                                    {subroute.label}
                                  </Link>
                                </NavigationMenuLink>
                              ))}
                            </li>
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  })}

                  <NavigationMenuItem>
                    <NavigationMenuLink
                      asChild
                      className={toClassName(
                        styles.link,
                        pathname.includes('/tiny-houses') &&
                          '!text-gray-300 bg-gray-50',
                      )}
                    >
                      <Link href={`/${locale}/tiny-houses`}>
                        {t('component.navbar.label.tinyHouses')}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div className="hidden md:flex min-w-lg items-center gap-x-6 justify-end">
              <Link
                href={`/${locale}/contact`}
                className={toClassName(styles.button, '')}
              >
                {t('component.navbar.label.getInContact')}
              </Link>
            </div>
          </div>
        </Container>

        {/* Temporarily hidden
        <Banner
          banner={{
            content: 'Houten Huis bouwt ook tiny houses.',
            url: `/tiny-houses`,
            isExternal: false,
          }}
          locale={locale}
        /> */}
      </div>
    </>
  );
};

export default NavBar;
